import React, { useState } from 'react';
import {
  Stack,
  Chip,
  Box,
  Alert,
  Typography,
  Grid,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import modelsService from '../../services/modelsService';
import BaseDialog from './BaseDialog';
import BaseTextField from './BaseTextField';
import BaseButton from './BaseButton';
import PromptChatDialog from './PromptChatDialog';

/**
 * Dialog pour créer un nouveau modèle de prompt
 * @param {boolean} open - État d'ouverture du dialog
 * @param {function} onClose - Callback de fermeture
 * @param {function} onSuccess - Callback de succès (optionnel)
 */
const NewModelDialog = ({ open, onClose, onSuccess }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [prompt, setPrompt] = useState('');
  const [participants, setParticipants] = useState([]);
  const [newParticipant, setNewParticipant] = useState({ displayName: '', title: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showPromptChat, setShowPromptChat] = useState(false);
  const { currentUser } = useAuth();
  const { addSystemNotification } = useNotifications();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Réinitialiser le formulaire
  const resetForm = () => {
    setTitle('');
    setDescription('');
    setTags([]);
    setTagInput('');
    setPrompt('');
    setParticipants([]);
    setNewParticipant({ displayName: '', title: '' });
    setError(null);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleDeleteTag = (tagToDelete) => {
    setTags(tags.filter((tag) => tag !== tagToDelete));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleAddParticipant = () => {
    if (!newParticipant.displayName.trim()) return;
    setParticipants([...participants, { ...newParticipant }]);
    setNewParticipant({ displayName: '', title: '' });
  };

  const handleRemoveParticipant = (index) => {
    const updatedParticipants = [...participants];
    updatedParticipants.splice(index, 1);
    setParticipants(updatedParticipants);
  };

  const handlePromptGenerated = (generatedPrompt) => {
    setPrompt(generatedPrompt);
    setShowPromptChat(false);
  };

  const openPromptChat = () => {
    setShowPromptChat(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentUser) {
      setError('Utilisateur non authentifié');
      return;
    }

    if (!title.trim() || !prompt.trim()) {
      setError('Le titre et le prompt sont requis');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const modelId = await modelsService.createModel({
        title: title.trim(),
        description: description.trim(),
        tags,
        prompt: prompt.trim(),
        participants
      }, currentUser.uid);

      // Callback de succès si fourni
      if (onSuccess) {
        onSuccess(modelId);
      }

      // Fermer le dialog et réinitialiser
      resetForm();
      onClose();

    } catch (error) {
      addSystemNotification('Erreur lors de la création du modèle:', 'error');
      setError('Erreur lors de la création du modèle');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Actions du dialog
  const dialogActions = (
    <>
      <BaseButton
        variant="outlined"
        onClick={handleClose}
        disabled={isSubmitting}
      >
        Annuler
      </BaseButton>
      <BaseButton
        gradient
        onClick={handleSubmit}
        disabled={isSubmitting || !title.trim() || !prompt.trim()}
        sx={{ minWidth: '140px' }}
      >
        {isSubmitting ? 'Création...' : 'Créer le modèle'}
      </BaseButton>
    </>
  );

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      title="✨ Nouveau Modèle de Prompt"
      subtitle="Créez un template pour personnaliser vos résumés"
      maxWidth="md"
      actions={dialogActions}
      customPaddingContent={isMobile ? 2 : 4}
      contentProps={{
        sx: {
          height: isMobile ? 'calc(100vh - 200px)' : 'auto',
          overflow: 'auto'
        }
      }}
    >
      <form onSubmit={handleSubmit}>
        <Grid container spacing={4}>
          {/* Titre du modèle */}
          <Grid item xs={12}>
            <Box sx={{
              background: 'white',
              borderRadius: '16px',
              p: 3,
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              border: '1px solid #e3f2fd'
            }}>
              <BaseTextField
                label="Titre du modèle"
                value={title}
                onChange={e => setTitle(e.target.value)}
                required
                fullWidth
                autoFocus
                placeholder="Ex: Résumé détaillé de réunion"
                InputProps={{
                  startAdornment: (
                    <Box sx={{ mr: 1, color: '#3F51B5', fontSize: '20px' }}>📝</Box>
                  )
                }}
              />
            </Box>
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <Box sx={{
              background: 'white',
              borderRadius: '16px',
              p: 3,
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              border: '1px solid #e3f2fd'
            }}>
              <BaseTextField
                label="Description (optionnelle)"
                value={description}
                onChange={e => setDescription(e.target.value)}
                multiline
                rows={2}
                fullWidth
                placeholder="Description optionnelle du modèle"
                InputProps={{
                  startAdornment: (
                    <Box sx={{ mr: 1, color: '#3F51B5', fontSize: '20px', alignSelf: 'flex-start', mt: 1 }}>📄</Box>
                  )
                }}
              />
            </Box>
          </Grid>

          {/* Tags */}
          <Grid item xs={12}>
            <Box sx={{
              background: 'white',
              borderRadius: '16px',
              p: 3,
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              border: '1px solid #e3f2fd'
            }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  mb: 3,
                  fontWeight: 700,
                  color: '#3F51B5',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                🏷️ Tags
              </Typography>

              <BaseTextField
                label="Ajouter un tag"
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={handleKeyPress}
                fullWidth
                placeholder="Ex: réunion, détaillé, format"
                helperText="Appuyez sur Entrée pour ajouter"
              />
              <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                {tags.map((tag, idx) => (
                  <Chip
                    key={idx}
                    label={tag}
                    onDelete={() => handleDeleteTag(tag)}
                    sx={{
                      background: 'linear-gradient(135deg, #3F51B5 0%, #5C6BC0 100%)',
                      color: 'white',
                      '& .MuiChip-deleteIcon': {
                        color: 'rgba(255,255,255,0.8)',
                        '&:hover': {
                          color: 'white'
                        }
                      }
                    }}
                    size="small"
                  />
                ))}
              </Stack>
            </Box>
          </Grid>

          {/* Participants */}
          <Grid item xs={12}>
            <Box sx={{
              background: 'white',
              borderRadius: '16px',
              p: 3,
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              border: '1px solid #e3f2fd'
            }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  mb: 3,
                  fontWeight: 700,
                  color: '#3F51B5',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                👥 Participants (optionnel)
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Définissez les participants types pour ce modèle. Ils seront pré-remplis lors de la création d'une réunion.
              </Typography>

              <Stack direction={isMobile ? "column" : "row"} spacing={2} sx={{ mb: 2.5 }}>
                <BaseTextField
                  label="Nom"
                  value={newParticipant.displayName}
                  onChange={e => setNewParticipant(prev => ({ ...prev, displayName: e.target.value }))}
                  size="medium"
                  sx={{ flex: 2 }}
                />
                <BaseTextField
                  label="Poste"
                  value={newParticipant.title}
                  onChange={e => setNewParticipant(prev => ({ ...prev, title: e.target.value }))}
                  size="medium"
                  sx={{ flex: 2 }}
                />
                <BaseButton 
                  variant="contained" 
                  size="medium"
                  onClick={handleAddParticipant}
                  disabled={!newParticipant.displayName.trim()}
                  sx={{ 
                    px: 3, 
                    py: 1.5,
                    minWidth: 'auto',
                    alignSelf: isMobile ? 'flex-start' : 'stretch',
                    borderRadius: '6px',
                    fontWeight: 500,
                    textTransform: 'none'
                  }}
                >
                  <AddIcon fontSize="small" />
                </BaseButton>
              </Stack>
              
              {participants.length > 0 && (
                <Box sx={{ 
                  maxHeight: 140, 
                  overflow: 'auto', 
                  background: '#ffffff',
                  border: '1px solid #e2e8f0', 
                  borderRadius: '8px',
                  '&::-webkit-scrollbar': {
                    width: '6px'
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f5f9'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#cbd5e1',
                    borderRadius: '3px'
                  }
                }}>
                  {participants.map((p, idx) => (
                    <Box 
                      key={idx} 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        p: 2.5, 
                        borderBottom: idx < participants.length - 1 ? '1px solid #f1f5f9' : 'none',
                        '&:hover': {
                          background: '#f8fafc'
                        },
                        transition: 'background 0.2s ease'
                      }}
                    >
                      <Box>
                        <Typography 
                          variant="body2" 
                          fontWeight={500} 
                          sx={{ 
                            color: '#1e293b', 
                            mb: 0.5,
                            fontSize: '14px'
                          }}
                        >
                          {p.displayName}
                        </Typography>
                        {p.title && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#64748b', 
                              fontSize: '13px'
                            }}
                          >
                            {p.title}
                          </Typography>
                        )}
                      </Box>
                      <IconButton 
                        size="small" 
                        onClick={() => handleRemoveParticipant(idx)}
                        sx={{
                          color: '#dc2626',
                          background: '#fef2f2',
                          borderRadius: '6px',
                          width: 32,
                          height: 32,
                          '&:hover': {
                            background: '#fee2e2',
                            transform: 'scale(1.05)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Grid>

          {/* Prompt système */}
          <Grid item xs={12}>
            <Box sx={{
              background: 'white',
              borderRadius: '16px',
              p: 3,
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              border: '1px solid #e3f2fd'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: '#3F51B5',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flex: 1
                  }}
                >
                  🤖 Prompt système
                </Typography>
                <Tooltip title="💬 Besoin d'aide ? Cliquez pour que l'IA vous guide !">
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'inline-block',
                      
                      // Bulle de BD principale avec effet typing
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: '-50px',
                        right: '-15px',
                        background: 'white',
                        border: '2px solid #FF6B6B',
                        borderRadius: '20px 20px 20px 4px',
                        padding: '8px 14px',
                        fontSize: '13px',
                        fontWeight: 'bold',
                        color: '#FF6B6B',
                        boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                        animation: 'typingBubble 8s ease-in-out infinite',
                        transformOrigin: 'bottom left',
                        zIndex: 10,
                        whiteSpace: 'nowrap',
                        minWidth: '60px',
                        textAlign: 'center'
                      },
                      
                      '@keyframes typingBubble': {
                          '0%': {
                            opacity: 0,
                            transform: 'scale(0.3) rotate(-8deg)',
                            content: '""'
                          },
                          '8%': {
                            opacity: 1,
                            transform: 'scale(1) rotate(0deg)',
                            content: '"💭"'
                          },
                          '12%': {
                            content: '"B"'
                          },
                          '14%': {
                            content: '"Be"'
                          },
                          '16%': {
                            content: '"Bes"'
                          },
                          '18%': {
                            content: '"Beso"'
                          },
                          '20%': {
                            content: '"Besoi"'
                          },
                          '22%': {
                            content: '"Besoin"'
                          },
                          '25%': {
                            content: '"Besoin d"'
                          },
                          '27%': {
                            content: '"Besoin d\'"'
                          },
                          '29%': {
                            content: '"Besoin d\'a"'
                          },
                          '31%': {
                            content: '"Besoin d\'ai"'
                          },
                          '33%': {
                            content: '"Besoin d\'aid"'
                          },
                          '35%': {
                            content: '"Besoin d\'aide"'
                          },
                          '38%': {
                            content: '"Besoin d\'aide p"'
                          },
                          '40%': {
                            content: '"Besoin d\'aide po"'
                          },
                          '42%': {
                            content: '"Besoin d\'aide pou"'
                          },
                          '44%': {
                            content: '"Besoin d\'aide pour"'
                          },
                          '47%': {
                            content: '"Besoin d\'aide pour g"'
                          },
                          '49%': {
                            content: '"Besoin d\'aide pour gé"'
                          },
                          '51%': {
                            content: '"Besoin d\'aide pour gén"'
                          },
                          '53%': {
                            content: '"Besoin d\'aide pour géné"'
                          },
                          '55%': {
                            content: '"Besoin d\'aide pour génér"'
                          },
                          '57%': {
                            content: '"Besoin d\'aide pour génére"'
                          },
                          '59%': {
                            content: '"Besoin d\'aide pour générer"'
                          },
                          '62%': {
                            content: '"Besoin d\'aide pour générer u"'
                          },
                          '64%': {
                            content: '"Besoin d\'aide pour générer un"'
                          },
                          '55%': {
                            content: '"Besoin d\'aide pour générer un p"'
                          },
                          '56%': {
                            content: '"Besoin d\'aide pour générer un pr"'
                          },
                          '57%': {
                            content: '"Besoin d\'aide pour générer un pro"'
                          },
                          '58%': {
                            content: '"Besoin d\'aide pour générer un prom"'
                          },
                          '59%': {
                            content: '"Besoin d\'aide pour générer un promp"'
                          },
                          '60%': {
                            content: '"Besoin d\'aide pour générer un prompt"'
                          },
                          '61%': {
                            content: '"Besoin d\'aide pour générer un prompt p"'
                          },
                          '62%': {
                            content: '"Besoin d\'aide pour générer un prompt pa"'
                          },
                          '63%': {
                            content: '"Besoin d\'aide pour générer un prompt par"'
                          },
                          '64%': {
                            content: '"Besoin d\'aide pour générer un prompt parf"'
                          },
                          '65%': {
                            content: '"Besoin d\'aide pour générer un prompt parfa"'
                          },
                          '66%': {
                            content: '"Besoin d\'aide pour générer un prompt parfai"'
                          },
                          '67%': {
                            content: '"Besoin d\'aide pour générer un prompt parfait"'
                          },
                          '68%': {
                            content: '"Besoin d\'aide pour générer un prompt parfait ?"'
                          },
                          '70%': {
                            content: '"Besoin d\'aide pour générer un prompt parfait ? 🤖"'
                          },
                          '90%': {
                            content: '"Besoin d\'aide pour générer un prompt parfait ? 🤖"',
                            opacity: 1,
                            transform: 'scale(1) rotate(0deg)'
                          },
                          '95%': {
                            opacity: 1,
                            transform: 'scale(1) rotate(0deg)'
                          },
                          '100%': {
                            opacity: 0,
                            transform: 'scale(0.3) rotate(8deg)',
                            content: '"💭"'
                          }
                        },
                      
                      '@keyframes iconBounce': {
                        '0%, 100%': {
                          transform: 'translateY(0px) scale(1)'
                        },
                        '50%': {
                          transform: 'translateY(-3px) scale(1.05)'
                        }
                      },
                      
                      '@keyframes iconGlow': {
                        '0%': {
                          boxShadow: '0 0 20px rgba(255, 107, 107, 0.5)'
                        },
                        '50%': {
                          boxShadow: '0 0 30px rgba(255, 107, 107, 0.8), 0 0 40px rgba(255, 140, 140, 0.4)'
                        },
                        '100%': {
                          boxShadow: '0 0 20px rgba(255, 107, 107, 0.5)'
                        }
                      }
                    }}
                  >
                    <IconButton
                      onClick={openPromptChat}
                      sx={{
                        position: 'relative',
                        zIndex: 1,
                        background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%)',
                        color: 'white',
                        animation: 'iconBounce 2.5s ease-in-out infinite, iconGlow 4s ease-in-out infinite',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #FF5555 0%, #FF7777 100%)',
                          transform: 'scale(1.2) translateY(-5px)',
                          boxShadow: '0 15px 35px rgba(255, 107, 107, 0.9)',
                          animation: 'none'
                        },
                        '&:active': {
                          transform: 'scale(0.95)'
                        },
                        '& .MuiSvgIcon-root': {
                          animation: 'iconBounce 2.2s ease-in-out infinite',
                          transformOrigin: 'center'
                        }
                      }}
                    >
                      <SmartToyIcon />
                    </IconButton>
                  </Box>
                </Tooltip>
              </Box>
              
              <BaseTextField
                label="Prompt système"
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                multiline
                rows={6}
                fullWidth
                required
                placeholder="Vous êtes un assistant qui résume des transcriptions de réunions..."
                helperText="Décrivez comment l'IA doit traiter les transcriptions, ou utilisez le générateur IA pour vous aider"
                InputProps={{
                  startAdornment: (
                    <Box sx={{ mr: 1, color: '#3F51B5', fontSize: '20px', alignSelf: 'flex-start', mt: 1 }}>🤖</Box>
                  ),
                  sx: { fontFamily: 'monospace' }
                }}
              />
            </Box>
          </Grid>

          {error && (
            <Grid item xs={12}>
              <Alert severity="error">
                {error}
              </Alert>
            </Grid>
          )}
        </Grid>
      </form>

      {/* Dialog de génération de prompt par IA */}
      <PromptChatDialog
        open={showPromptChat}
        onClose={() => setShowPromptChat(false)}
        onPromptGenerated={handlePromptGenerated}
      />
    </BaseDialog>
  );
};

export default NewModelDialog; 